'use client';

import { useEffect, useMemo, useState } from 'react';

import {
  useGetJobDeliverablesQuery,
  useGetJobsQuery,
  useLazyGetJobDeliverablesQuery,
} from '@/features/jobs/api/jobsApi';
import { useUpdateTaskMutation } from '@/features/tasks/api/tasksApi';
import { Drawer, MainButton, SecondaryButton, SmartInput } from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobTasksComponents.module.scss';

import type { JobDeliverable } from '@/features/jobs/types';
import type { Task, UpdateTaskPayload } from '@/features/tasks/types';

type MoveMode = 'deliverable' | 'job';

type MoveTaskDrawerProps = {
  open: boolean;
  task: Task | null;
  currentJobId: string;
  currentLocationLabel: string;
  onClose: () => void;
  onMoved?: () => void;
};

function extractDeliverables(payload: unknown): JobDeliverable[] {
  const data = payload as
    | { results?: JobDeliverable[] }
    | JobDeliverable[]
    | null;
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  return data.results ?? [];
}

export function MoveTaskDrawer({
  open,
  task,
  currentJobId,
  currentLocationLabel,
  onClose,
  onMoved,
}: MoveTaskDrawerProps) {
  return (
    <Drawer open={open} onClose={onClose} anchor="right" width={420}>
      {open && task ? (
        <MoveTaskDrawerContent
          key={task.id}
          task={task}
          currentJobId={currentJobId}
          currentLocationLabel={currentLocationLabel}
          onClose={onClose}
          onMoved={onMoved}
        />
      ) : null}
    </Drawer>
  );
}

function MoveTaskDrawerContent({
  task,
  currentJobId,
  currentLocationLabel,
  onClose,
  onMoved,
}: Omit<MoveTaskDrawerProps, 'open'> & { task: Task }) {
  const [mode, setMode] = useState<MoveMode>('deliverable');
  const [targetJobId, setTargetJobId] = useState('');
  const [targetDeliverableId, setTargetDeliverableId] = useState('');
  const [formError, setFormError] = useState('');

  const [updateTask, { isLoading: isMoving }] = useUpdateTaskMutation();
  const { data: jobsResponse, isFetching: isJobsLoading } = useGetJobsQuery(
    { page_size: 100 },
    { skip: false },
  );
  const {
    data: currentDeliverablesResponse,
    isFetching: isCurrentDeliverablesLoading,
  } = useGetJobDeliverablesQuery(
    { jobId: currentJobId, params: { page_size: 100 } },
    { skip: !currentJobId },
  );
  const [
    loadTargetDeliverables,
    {
      data: targetDeliverablesResponse,
      isFetching: isTargetDeliverablesLoading,
    },
  ] = useLazyGetJobDeliverablesQuery();

  useEffect(() => {
    if (mode !== 'job' || !targetJobId) {
      return;
    }
    void loadTargetDeliverables(
      { jobId: targetJobId, params: { page_size: 100 } },
      true,
    );
  }, [loadTargetDeliverables, mode, targetJobId]);

  const jobs = useMemo(() => {
    const results = (jobsResponse?.data?.results ?? []).filter(
      (job) => job.id !== currentJobId,
    );
    return results.map((job) => ({
      value: job.id,
      label: job.name || `Job ${job.job_number ?? ''}`.trim(),
    }));
  }, [currentJobId, jobsResponse?.data?.results]);

  const currentDeliverables = useMemo(
    () => extractDeliverables(currentDeliverablesResponse?.data),
    [currentDeliverablesResponse?.data],
  );
  const targetDeliverables = useMemo(
    () => extractDeliverables(targetDeliverablesResponse?.data),
    [targetDeliverablesResponse?.data],
  );

  const destinationDeliverableOptions = useMemo(() => {
    const source =
      mode === 'deliverable'
        ? currentDeliverables.filter(
            (deliverable) => deliverable.id !== task.deliverable_id,
          )
        : targetDeliverables;

    return source.map((deliverable) => ({
      value: deliverable.id,
      label: deliverable.name,
    }));
  }, [currentDeliverables, mode, targetDeliverables, task.deliverable_id]);

  const isLoadingDeliverables =
    mode === 'deliverable'
      ? isCurrentDeliverablesLoading
      : isTargetDeliverablesLoading;

  const handleMove = async () => {
    const nextJobId = mode === 'deliverable' ? currentJobId : targetJobId;
    if (!nextJobId) {
      setFormError('Select a destination job.');
      return;
    }
    if (!targetDeliverableId) {
      setFormError('Select a destination deliverable.');
      return;
    }

    try {
      setFormError('');
      const payload: UpdateTaskPayload = {
        job_id: nextJobId,
        deliverable_id: targetDeliverableId,
      };
      await updateTask({
        taskId: task.id,
        payload,
      }).unwrap();
      notify({ type: 'success', message: 'Task moved successfully.' });
      onMoved?.();
      onClose();
    } catch (error) {
      const message = getErrorMessage(error);
      setFormError(message);
      notify({ type: 'error', message });
    }
  };

  return (
    <div className={styles.moveTaskDrawer}>
      <header className={styles.moveTaskDrawerHeader}>
        <h3 className={styles.moveTaskDrawerTitle}>Move Task</h3>
      </header>

      <div className={styles.moveTaskDrawerBody}>
        <div className={styles.moveTaskCurrentLocation}>
          <p className={styles.moveTaskLabel}>Current location</p>
          <p className={styles.moveTaskValue}>
            {currentLocationLabel}
            <br />
            {task.title || 'Untitled task'}
          </p>
        </div>

        <div className={styles.moveTaskModeGroup}>
          <p className={styles.moveTaskLabel}>Move</p>
          <button
            type="button"
            className={`${styles.moveTaskModeButton} ${
              mode === 'deliverable' ? styles.moveTaskModeButtonActive : ''
            }`}
            onClick={() => {
              setMode('deliverable');
              setTargetJobId('');
              setTargetDeliverableId('');
              setFormError('');
            }}
          >
            Move to another deliverable
          </button>
          <button
            type="button"
            className={`${styles.moveTaskModeButton} ${
              mode === 'job' ? styles.moveTaskModeButtonActive : ''
            }`}
            onClick={() => {
              setMode('job');
              setTargetDeliverableId('');
              setFormError('');
            }}
          >
            Move to another job
          </button>
        </div>

        {mode === 'job' ? (
          <SmartInput
            type="select"
            name="move-task-target-job"
            label="Job"
            value={targetJobId}
            options={jobs}
            readOnly={isJobsLoading}
            onChange={(nextValue) => {
              setTargetJobId(String(nextValue || ''));
              setTargetDeliverableId('');
              setFormError('');
            }}
          />
        ) : null}

        <SmartInput
          type="select"
          name="move-task-target-deliverable"
          label="Deliverable"
          value={targetDeliverableId}
          options={destinationDeliverableOptions}
          error={formError || undefined}
          readOnly={isLoadingDeliverables || (mode === 'job' && !targetJobId)}
          onChange={(nextValue) => {
            setTargetDeliverableId(String(nextValue || ''));
            setFormError('');
          }}
        />
      </div>

      <footer className={styles.moveTaskDrawerFooter}>
        <SecondaryButton ariaLabel="Cancel move task" onClick={onClose}>
          Cancel
        </SecondaryButton>
        <MainButton
          ariaLabel="Move task"
          isLoading={isMoving}
          onClick={handleMove}
        >
          Move Task
        </MainButton>
      </footer>
    </div>
  );
}
